import { supabase } from '../lib/supabase';

/**
 * Utility functions to verify authentication functionality
 */

/**
 * Test user registration
 * @returns Promise with result of registration attempt
 */
export const testRegistration = async () => {
  try {
    // Generate a unique test email
    const testEmail = `test-${Date.now()}@example.com`;
    const testPassword = 'password123';
    
    console.log('Testing registration with:', testEmail);
    
    // Attempt to register
    const { data, error } = await supabase.auth.signUp({
      email: testEmail,
      password: testPassword,
      options: {
        data: {
          name: 'Test User',
          role: 'athlete',
          sport: 'Testing'
        }
      }
    });
    
    if (error) {
      console.error('Registration failed:', error.message);
      return { success: false, error: error.message };
    }
    
    console.log('Registration successful:', data);
    return { success: true, data };
  } catch (err) {
    console.error('Unexpected error during registration test:', err);
    return { success: false, error: err };
  }
};

/**
 * Test profile update
 * @param userId The user ID to update
 * @returns Promise with result of update attempt
 */
export const testProfileUpdate = async (userId: string) => {
  try {
    console.log('Testing profile update for user:', userId);
    
    // Attempt to update profile
    const { data, error } = await supabase
      .from('profiles')
      .update({
        bio: `Profile updated at ${new Date().toISOString()}`,
        updated_at: new Date().toISOString()
      })
      .eq('id', userId);
    
    if (error) {
      console.error('Profile update failed:', error.message);
      return { success: false, error: error.message };
    }
    
    console.log('Profile update successful');
    return { success: true, data };
  } catch (err) {
    console.error('Unexpected error during profile update test:', err);
    return { success: false, error: err };
  }
};

/**
 * Verify Supabase configuration
 * @returns Object with configuration status
 */
export const verifySupabaseConfig = () => {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  
  const isConfigured = 
    supabaseUrl && 
    supabaseKey && 
    !supabaseUrl.includes('your-project-url') &&
    !supabaseKey.includes('your_supabase_anon_key');
  
  return {
    isConfigured,
    supabaseUrl: supabaseUrl || 'Not configured',
    hasAnonKey: !!supabaseKey
  };
};

/**
 * Run a complete verification of auth functionality
 */
export const runAuthVerification = async () => {
  console.group('🔍 Auth Verification');
  
  // Step 1: Check configuration
  console.log('Step 1: Verifying Supabase configuration...');
  const configStatus = verifySupabaseConfig();
  console.log('Configuration status:', configStatus);
  
  if (!configStatus.isConfigured) {
    console.error('❌ Supabase is not properly configured. Please check your .env file.');
    console.groupEnd();
    return { success: false, error: 'Supabase not configured' };
  }
  
  // Step 2: Test connection
  console.log('Step 2: Testing Supabase connection...');
  try {
    const { data, error } = await supabase.from('profiles').select('count').limit(1);
    if (error) {
      console.error('❌ Connection test failed:', error.message);
      console.groupEnd();
      return { success: false, error: error.message };
    }
    console.log('✅ Connection successful');
  } catch (err) {
    console.error('❌ Connection test failed with exception:', err);
    console.groupEnd();
    return { success: false, error: err };
  }
  
  // Step 3: Test registration
  console.log('Step 3: Testing user registration...');
  const registrationResult = await testRegistration();
  
  if (!registrationResult.success) {
    console.error('❌ Registration test failed');
    console.groupEnd();
    return { success: false, error: registrationResult.error };
  }
  console.log('✅ Registration test passed');
  
  // We don't test profile update here as it requires an authenticated user
  
  console.log('✅ All verification steps completed successfully');
  console.groupEnd();
  return { success: true };
};